"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import ProductCardList from "@/components/ProductCardList";
import GlobalAccordion from "@/components/CustomAccordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ProductType as CourseType } from "@/common/course/types";
import { ElementClicked } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementClicked";
import {
  CommonVariants,
  ElementType,
  FormatSubType,
} from "@/constants/tagular/main";
import makeEvent from "@/hooks/eventing/makeEvent";
import { EVENTS } from "@/hooks/eventing/types/events";
import { ElementViewed } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementViewed";
import { translateVariant } from "@/utils/tagular/dataTranslationMatrices";
import type {
  Category,
  ProductCardTabsAndPillsByUUIDProps,
  Subcategory,
} from "./types";
import useIsMobile from "@/hooks/useIsMobile";
import useTranslations from "@/i18n/useTranslations";
import { sanitizeTabValue } from "@/utils/sanitizeTabValue";
import { cn } from "@/lib/utils";

// Helper function to create unique tab values with location
const createTabValue = (label: string, location?: string): string => {
  const sanitizedLabel = sanitizeTabValue(label);
  return location
    ? `${sanitizedLabel}-${sanitizeTabValue(location)}`
    : sanitizedLabel;
};

function ProductCardTabsAndPillsByUUIDClient({
  title,
  titleLeading,
  className,
  categories,
  location,
  cardFormatSubtype = FormatSubType.HorizontallyStacked,
}: ProductCardTabsAndPillsByUUIDProps) {
  const tExpand = useTranslations("ExpandableBlock");
  const filteredCategories = useMemo(() => {
    return (
      categories?.filter(
        (category) => category.productType !== CourseType.BOOTCAMP,
      ) || []
    );
  }, [categories]);

  const [activeCategory, setActiveCategory] = useState<Category | undefined>(
    () => filteredCategories[0],
  );

  const isMobile = useIsMobile();

  useEffect(() => {
    const first = filteredCategories[0];
    setActiveCategory((prev) => {
      if (!first) return undefined;
      if (!prev) return first;
      const stillThere = filteredCategories.find(
        (c) => c.categoryLabel === prev.categoryLabel,
      );
      return stillThere ?? first;
    });
  }, [filteredCategories]);

  const [openAccordions, setOpenAccordions] = useState<Record<string, boolean>>(
    {},
  );

  const [activeSubcategory, setActiveSubcategory] = useState<
    Subcategory | undefined
  >(() => filteredCategories[0]?.subcategories?.[0]);

  useEffect(() => {
    const subs = activeCategory?.subcategories;
    if (!subs?.length) {
      setActiveSubcategory(undefined);
      return;
    }
    setActiveSubcategory((prev) => {
      if (!prev) return subs[0];
      const stillThere = subs.find(
        (s) => s.subcategoryLabel === prev.subcategoryLabel,
      );
      return stillThere ?? subs[0];
    });
  }, [activeCategory]);

  const createAccordionKey = useCallback(
    (categoryLabel: string, subcategoryLabel: string) => {
      return `${createTabValue(categoryLabel, location)}::${sanitizeTabValue(
        subcategoryLabel,
      )}`;
    },
    [location],
  );

  const fallbackAccordionTitle = useCallback(
    (isOpen: boolean, subcategory: Subcategory, category: Category) => {
      const prefix = isOpen ? tExpand("showLess") : tExpand("viewMore");
      return `${prefix} ${subcategory.subcategoryLabel.toLowerCase()} ${category.categoryLabel.toLowerCase()}`;
    },
    [tExpand],
  );

  const getAccordionTitle = useCallback(
    (isOpen: boolean, subcategory: Subcategory, category: Category) => {
      const override = isOpen
        ? subcategory.showLessLabel
        : subcategory.viewMoreLabel;
      if (override && override.trim().length > 0) {
        return override;
      }
      return fallbackAccordionTitle(isOpen, subcategory, category);
    },
    [fallbackAccordionTitle],
  );

  const handleCategoryChange = (sanitizedValue: string) => {
    setOpenAccordions({});
    const category = filteredCategories?.find(
      (cat) => createTabValue(cat.categoryLabel, location) === sanitizedValue,
    );
    setActiveCategory(category);
  };

  const hasMoreProducts = (products: any[]) => {
    const initialDisplayCount = 4;
    return products && products.length > initialDisplayCount;
  };

  const shouldShowAccordion = (subcategory: Subcategory | undefined) => {
    return !isMobile && hasMoreProducts(subcategory?.products || []);
  };

  const generateEventData = useCallback(
    (text: string) => {
      const webElement: ElementClicked["webElement"] = {
        elementType: ElementType.Link,
        location: location,
        position: "1",
        text: text,
        variant: activeCategory
          ? translateVariant(activeCategory.categoryLabel)
          : CommonVariants.AllProductsMixed,
        htmlId: "view-more-link",
      };
      return { webElement };
    },
    [activeCategory, location],
  );

  const sendViewedEvent = useCallback(
    (title: string) => {
      const event = makeEvent<ElementViewed, EVENTS>(generateEventData(title));
      event<ElementViewed>(EVENTS.ElementViewed)();
    },
    [generateEventData],
  );

  const sendClickedEvent = useCallback(
    (title: string) => {
      const event = makeEvent<ElementClicked, EVENTS>(generateEventData(title));
      event<ElementClicked>(EVENTS.ElementClicked)();
    },
    [generateEventData],
  );

  const resolvedTitleLeading = titleLeading !== undefined ? titleLeading : null;

  return (
    <div
      className={cn(
        "py-8 lg:py-20 mx-break-out flex justify-center bg-putty-100",
        className,
      )}
    >
      <div className="container">
        <h2 className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xl lg:text-3xl font-bold mb-4">
          {resolvedTitleLeading ? (
            <span className="inline-flex shrink-0 items-center">
              {resolvedTitleLeading}
            </span>
          ) : null}
          <span>{title}</span>
        </h2>
        <Tabs
          value={
            activeCategory
              ? createTabValue(activeCategory.categoryLabel, location)
              : undefined
          }
          onValueChange={handleCategoryChange}
          className="border rounded-xl"
        >
          <div className="hidden lg:block rounded-t-xl">
            <TabsList>
              {filteredCategories?.map((category) => {
                const sanitizedValue = createTabValue(
                  category.categoryLabel,
                  location,
                );

                return (
                  <TabsTrigger
                    key={category.categoryLabel}
                    value={sanitizedValue}
                    className="no-underline text-sm text-primary hover:text-primary data-[state=active]:font-bold bg-putty-100 rounded-none first:rounded-tl-xl last:rounded-tr-xl"
                  >
                    {category.categoryLabel}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>
          <div className="lg:hidden">
            <Select
              value={
                activeCategory
                  ? createTabValue(activeCategory.categoryLabel, location)
                  : undefined
              }
              onValueChange={handleCategoryChange}
            >
              <SelectTrigger className="h-auto rounded-none bg-primary-400 text-base text-white hover:text-white no-underline p-4 border-none">
                <SelectValue placeholder={activeCategory?.categoryLabel} />
              </SelectTrigger>
              <SelectContent>
                {filteredCategories?.map((category) => {
                  const sanitizedValue = createTabValue(
                    category.categoryLabel,
                    location,
                  );
                  return (
                    <SelectItem key={sanitizedValue} value={sanitizedValue}>
                      {category.categoryLabel}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {filteredCategories?.map((category) => {
            const sanitizedValue = createTabValue(
              category.categoryLabel,
              location,
            );

            return (
              <TabsContent
                key={`${category.categoryLabel}-content`}
                value={sanitizedValue}
                className={`bg-white p-4 mt-0 border-t rounded-b-xl ${activeCategory?.categoryLabel == category.categoryLabel ? "block" : "hidden"}`}
                forceMount={true}
              >
                <Tabs
                  value={
                    activeSubcategory
                      ? sanitizeTabValue(activeSubcategory.subcategoryLabel)
                      : undefined
                  }
                  onValueChange={(sanitizedValue) => {
                    // Find the original subcategory that matches the sanitized value
                    const originalSubcategory = category?.subcategories?.find(
                      (sub) =>
                        sanitizeTabValue(sub.subcategoryLabel) ===
                        sanitizedValue,
                    );
                    if (originalSubcategory) {
                      setActiveSubcategory(originalSubcategory);
                    }
                  }}
                >
                  <TabsList className="overflow-x-scroll justify-start h-auto pt-4 gap-4">
                    {category?.subcategories?.map((subcategory) => {
                      const sanitizedSubcategoryValue = sanitizeTabValue(
                        subcategory.subcategoryLabel,
                      );
                      if (subcategory.subcategoryLabel.trim() === "") {
                        return null;
                      }

                      return (
                        <TabsTrigger
                          key={sanitizedSubcategoryValue}
                          value={sanitizedSubcategoryValue}
                          className="grow-0 rounded-xl border border-primary no-underline py-1 px-2 text-primary text-xs font-normal bg-putty hover:bg-primary hover:text-white data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:hover:text-white"
                        >
                          {subcategory.subcategoryLabel}
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                  {category?.subcategories?.map((subcategory) => {
                    const sanitizedSubcategoryValue = sanitizeTabValue(
                      subcategory.subcategoryLabel,
                    );
                    const accordionKey = createAccordionKey(
                      category.categoryLabel,
                      subcategory.subcategoryLabel,
                    );
                    const isAccordionOpen =
                      openAccordions[accordionKey] ?? false;
                    const accordionTitle = getAccordionTitle(
                      isAccordionOpen,
                      subcategory,
                      category,
                    );

                    return (
                      <TabsContent
                        key={`${category.categoryLabel}-${sanitizedSubcategoryValue}-content`}
                        value={sanitizedSubcategoryValue}
                        forceMount={true}
                        className={`${activeSubcategory?.subcategoryLabel == subcategory.subcategoryLabel ? "block" : "hidden"}`}
                      >
                        <ProductCardList
                          products={
                            isMobile
                              ? subcategory.products || []
                              : subcategory.products?.slice(0, 4) || []
                          }
                          location={location}
                          cardFormatSubType={cardFormatSubtype}
                          shouldScroll={isMobile ? true : !isAccordionOpen}
                        />
                        {shouldShowAccordion(subcategory) && (
                          <div className="w-full justify-center">
                            <GlobalAccordion
                              title={accordionTitle}
                              openOnInit={false}
                              titleClassName="inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 text-primary underline-offset-4 h-10 px-4 text-base not-prose underline hover:no-underline hover:font-bold"
                              titleWrapperClasses="self-center"
                              onStateChange={(isOpen, title) => {
                                const nextIsOpen = !isOpen;
                                setOpenAccordions((prev) => ({
                                  ...prev,
                                  [accordionKey]: nextIsOpen,
                                }));
                                sendClickedEvent(title);
                              }}
                              onViewed={(title) => sendViewedEvent(title)}
                            >
                              <ProductCardList
                                className="pt-0"
                                products={subcategory.products?.slice(4) || []}
                                location={location}
                                shouldScroll={false}
                              />
                            </GlobalAccordion>
                          </div>
                        )}
                      </TabsContent>
                    );
                  })}
                </Tabs>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </div>
  );
}

export default ProductCardTabsAndPillsByUUIDClient;
